import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Power, Timer, CalendarClock, Trash2, Loader2, ChevronDown, RotateCcw, Gauge } from "lucide-react";
import BrandLoader from "@/components/BrandLoader";

const DAYS = ["L", "M", "M", "J", "V", "S", "D"]; // 0=Lun … 6=Dim

// A4 — paramètres métier par type (backend stocke s/mV, l'UI affiche min/V). Backend = autorité.
const A4_FIELDS = {
  exces_vitesse: [{ key: "speed_limit_kmh", label: "Alerter au-dessus de", unit: "km/h", toDisp: (v) => v, toStore: (v) => Math.round(v), step: "1", min: "10" }],
  // Excès selon la limitation routière (OSM/ODbL) — marge de tolérance + durée minimale de persistance.
  // Bornes serveur = autorité : marge 0–50 km/h, durée 0–86400 s. Le délai de répétition (ci-dessous) ne masque jamais le rapport.
  exces_route: [
    { key: "min_duration_s", label: "Durée minimale", unit: "s", toDisp: (v) => v ?? 0, toStore: (v) => Math.round(v), step: "1", min: "0", max: "86400" },
  ],
  arret_prolonge: [{ key: "min_duration_s", label: "Déclencher après", unit: "min", toDisp: (s) => s / 60, toStore: (m) => Math.round(m * 60), step: "1", min: "1" }],
  ralenti: [{ key: "min_duration_s", label: "Déclencher après", unit: "min", toDisp: (s) => s / 60, toStore: (m) => Math.round(m * 60), step: "0.5", min: "0.5" }],
  batterie: [
    { key: "open_below_mv", label: "Alerter sous", unit: "V", toDisp: (mv) => mv / 1000, toStore: (v) => Math.round(v * 1000), step: "0.1", min: "6" },
    { key: "close_above_mv", label: "Considérer rétablie à", unit: "V", toDisp: (mv) => mv / 1000, toStore: (v) => Math.round(v * 1000), step: "0.1", min: "6" },
  ],
  deconnexion: [{ key: "offline_after_s", label: "Déclarer hors ligne après", unit: "min", toDisp: (s) => s / 60, toStore: (m) => Math.round(m * 60), step: "1", min: "1" }],
};

// exces_route — marge en km/h (fixe, rétrocompatible) OU en % de la limitation routière.
// Seuil exact = limite × (100 + %) / 100. Validation non négative. Backend = autorité.
const ROUTE_EX_BASES = [30, 50, 70, 90, 110];
const fmtKmh = (n) => (Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100));
function ExcesRouteMargin({ row, onSave }) {
  const p = row.params || {};
  const [mode, setMode] = useState(p.margin_mode === "pct" ? "pct" : "kmh");
  const [kmh, setKmh] = useState(p.margin_kmh ?? 0);
  const [pct, setPct] = useState(p.margin_pct ?? 0);
  useEffect(() => {
    setMode(p.margin_mode === "pct" ? "pct" : "kmh");
    setKmh(p.margin_kmh ?? 0);
    setPct(p.margin_pct ?? 0);
  }, [p.margin_mode, p.margin_kmh, p.margin_pct]);

  const commit = (nextMode, nextKmh, nextPct) => {
    const val = nextMode === "pct" ? Number(nextPct) : Number(nextKmh);
    if (!Number.isFinite(val) || val < 0) { toast.error("Valeur non négative requise"); return; }
    const max = nextMode === "pct" ? 100 : 50;
    if (val > max) { toast.error(`Maximum ${max}${nextMode === "pct" ? " %" : " km/h"}`); return; }
    onSave({ ...p, margin_mode: nextMode,
      margin_kmh: Math.round(nextMode === "kmh" ? val : (p.margin_kmh ?? 0)),
      margin_pct: Math.round(nextMode === "pct" ? val : (p.margin_pct ?? 0)) });
  };

  return (
    <div className="flex flex-col gap-2 pb-2 border-b border-border/60" data-testid="params-exces_route-margin">
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <Gauge className="w-4 h-4 text-[#17A2B8]" />
        <span>Marge de dépassement :</span>
        <select value={mode} data-testid="exces_route-margin-mode"
          onChange={(e) => { setMode(e.target.value); commit(e.target.value, kmh, pct); }}
          className="rounded-md border border-border bg-secondary/40 px-2 py-1 text-sm outline-none focus:border-[#17A2B8]">
          <option value="kmh">Fixe (km/h)</option>
          <option value="pct">Pourcentage de la limitation (%)</option>
        </select>
        {mode === "kmh" ? (
          <>
            <input type="number" step="1" min="0" max="50" value={kmh} data-testid="exces_route-margin-kmh"
              onChange={(e) => setKmh(e.target.value)} onBlur={() => commit("kmh", kmh, pct)}
              className="w-24 rounded-md border border-border bg-secondary/40 px-2 py-1 text-sm outline-none focus:border-[#17A2B8]" />
            <span className="text-muted-foreground">km/h</span>
          </>
        ) : (
          <>
            <input type="number" step="1" min="0" max="100" value={pct} data-testid="exces_route-margin-pct"
              onChange={(e) => setPct(e.target.value)} onBlur={() => commit("pct", kmh, pct)}
              className="w-24 rounded-md border border-border bg-secondary/40 px-2 py-1 text-sm outline-none focus:border-[#17A2B8]" />
            <span className="text-muted-foreground">%</span>
          </>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground" data-testid="exces_route-margin-example">
        {mode === "pct"
          ? `Seuil = limitation × (100 + ${Number(pct) || 0}) / 100. Exemples : ${ROUTE_EX_BASES.map((b) => `${b}→${fmtKmh(b * (100 + (Number(pct) || 0)) / 100)}`).join(", ")} km/h. Déclenchement strict si vitesse > seuil.`
          : `Seuil = limitation + ${Number(kmh) || 0} km/h. Exemples : ${ROUTE_EX_BASES.map((b) => `${b}→${b + (Number(kmh) || 0)}`).join(", ")} km/h. Déclenchement strict si vitesse > seuil.`}
      </p>
    </div>
  );
}


const statusOf = (s) => {
  if (!s.active) return { label: "Coupée", color: "#EF4444" };
  if (s.snooze_until && new Date(s.snooze_until) > new Date()) return { label: "En veille", color: "#F59E0B" };
  if (!s.always_on) return { label: "Planifiée", color: "#8B5CF6" };
  return { label: "Active", color: "#10B981" };
};

export default function AlertSettingsModal({ open, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  // A3 — portée du réglage : global | company | pool | vehicle + entité concernée
  const [scopeType, setScopeType] = useState("global");
  const [scopeId, setScopeId] = useState("");
  const [companies, setCompanies] = useState([]);
  const [pools, setPools] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/companies").then((r) => setCompanies(r.data || [])).catch(() => {});
    api.get("/pools").then((r) => setPools(r.data || [])).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {});
  }, [open]);

  const scopeQS = useCallback(() => (scopeType === "global" ? "?scope_type=global" : `?scope_type=${scopeType}&scope_id=${encodeURIComponent(scopeId)}`), [scopeType, scopeId]);
  const scopeReady = scopeType === "global" || !!scopeId;

  const load = useCallback(async () => {
    if (!scopeReady) { setRows([]); setLoading(false); return; }
    setLoading(true);
    try { const r = await api.get(`/alert-settings${scopeQS()}`); setRows(r.data); }
    catch (e) { toast.error(e.response?.status === 403 ? "Portée réservée aux administrateurs" : "Impossible de charger les réglages"); }
    finally { setLoading(false); }
  }, [scopeQS, scopeReady]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const patch = async (type, body, silent) => {
    setRows((rs) => rs.map((r) => (r.type === type ? { ...r, ...body, has_override: true } : r)));
    try {
      const r = await api.put(`/alert-settings/${type}${scopeQS()}`, body);
      setRows((rs) => rs.map((x) => (x.type === type ? { ...x, ...r.data, has_override: true } : x)));
      if (!silent) toast.success("Réglage enregistré");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409) toast.error(typeof d === "object" ? (d.error || "Conflit d'override groupe") : "Conflit d'override groupe");
      else if (e.response?.status === 422) toast.error(typeof d === "string" ? d : "Valeur invalide");
      else toast.error(e.response?.status === 403 ? "Réservé aux administrateurs" : "Erreur");
      load();
    }
  };

  const saveParam = (row, field, dispValue) => {
    const n = Number(dispValue);
    if (!Number.isFinite(n)) { toast.error("Valeur invalide"); return; }
    const cur = { ...(row.params || {}) };
    cur[field.key] = field.toStore(n);
    patch(row.type, { params: cur });
  };

  const revert = async (row) => {
    try {
      await api.delete(`/alert-settings/${row.type}/override${scopeQS()}`);
      toast.success("Retour au réglage hérité");
      load();
    } catch { toast.error("Erreur"); }
  };

  const clearHistory = async (row) => {
    if (!window.confirm(`Supprimer les ${row.count} alerte(s) « ${row.label} » de l'historique ?`)) return;
    try { const r = await api.delete(`/alerts/by-type/${row.type}`); toast.success(`${r.data.deleted} alerte(s) supprimée(s)`); load(); }
    catch { toast.error("Erreur"); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[3000] flex items-start justify-center bg-black/60 p-3 overflow-y-auto" data-testid="alert-settings-modal">
      <div className="bg-card border border-border rounded-xl w-full max-w-3xl my-6 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border sticky top-0 bg-card rounded-t-xl">
          <div>
            <h2 className="text-lg font-bold">Gestion des alertes</h2>
            <p className="text-xs text-muted-foreground">Portée hiérarchique : Véhicule &gt; Groupe &gt; Société &gt; Global. Le niveau le plus spécifique s'applique.</p>
          </div>
          <button onClick={onClose} data-testid="alert-settings-close" className="text-muted-foreground hover:text-foreground p-1"><X className="w-5 h-5" /></button>
        </div>

        {/* A3 — sélecteur de portée + entité */}
        <div className="flex items-center gap-2 flex-wrap px-5 py-3 border-b border-border bg-secondary/20">
          <span className="text-xs font-semibold text-muted-foreground">Portée :</span>
          <select value={scopeType} data-testid="scope-type" onChange={(e) => { setScopeType(e.target.value); setScopeId(""); }}
            className="text-xs rounded-md border border-border bg-card px-2 py-1.5">
            <option value="global">Global (toute la flotte)</option>
            <option value="company">Société</option>
            <option value="pool">Groupe</option>
            <option value="vehicle">Véhicule</option>
          </select>
          {scopeType !== "global" && (
            <select value={scopeId} data-testid="scope-entity" onChange={(e) => setScopeId(e.target.value)}
              className="text-xs rounded-md border border-border bg-card px-2 py-1.5 min-w-[180px]">
              <option value="">— Choisir {scopeType === "company" ? "une société" : scopeType === "pool" ? "un groupe" : "un véhicule"} —</option>
              {(scopeType === "company" ? companies : scopeType === "pool" ? pools : vehicles).map((it) => (
                <option key={it.id} value={it.id}>{it.name}{it.plate ? ` (${it.plate})` : ""}</option>
              ))}
            </select>
          )}
        </div>

        <div className="p-4 space-y-2.5 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-10"><BrandLoader size={44} rings={false} label="Chargement des réglages…" /></div>
          ) : !scopeReady ? (
            <div className="text-center text-sm text-muted-foreground py-10" data-testid="scope-hint">Choisissez l'entité concernée pour afficher ses réglages.</div>
          ) : rows.map((row) => {
            const st = statusOf(row);
            const isOpen = expanded === row.type;
            return (
              <div key={row.type} className="border border-border rounded-lg" data-testid={`alert-set-${row.type}`}>
                <div className="flex items-center gap-3 px-3 py-2.5 flex-wrap">
                  {/* Activé */}
                  <button onClick={() => patch(row.type, { active: !row.active })} data-testid={`toggle-active-${row.type}`}
                    className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${row.active ? "bg-[#10B981]" : "bg-zinc-400"}`} title={row.active ? "Coupée l'alerte" : "Activer l'alerte"}>
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${row.active ? "translate-x-5" : ""}`} />
                  </button>
                  <div className="flex-1 min-w-[150px]">
                    <div className="text-sm font-semibold">{row.label}</div>
                    <div className="text-[11px] text-muted-foreground">{row.count} alerte(s) dans l'historique</div>
                  </div>
                  <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full shrink-0" style={{ color: st.color, background: `${st.color}20` }} data-testid={`status-${row.type}`}>{st.label}</span>
                  {scopeType !== "global" && (
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${row.has_override ? "text-[#F5821F] bg-[#F5821F]/15" : "text-muted-foreground bg-muted"}`} data-testid={`scope-badge-${row.type}`}>
                      {row.has_override ? "Surcharge" : "Hérité"}
                    </span>
                  )}
                  <button onClick={() => setExpanded(isOpen ? null : row.type)} data-testid={`expand-${row.type}`}
                    className="flex items-center gap-1 text-xs text-[#2ec5db] hover:underline shrink-0"><ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} /> Réglages</button>
                </div>

                {isOpen && (
                  <div className="border-t border-border px-3 py-3 space-y-3 bg-secondary/20">
                    {/* A4 — Seuils/durées métier (uniquement les types concernés) */}
                    {row.type === "exces_route" && <ExcesRouteMargin row={row} onSave={(params) => patch(row.type, { params })} />}
                    {A4_FIELDS[row.type] && (
                      <div className="flex flex-col gap-2 pb-1 border-b border-border/60" data-testid={`params-${row.type}`}>
                        {A4_FIELDS[row.type].map((f) => (
                          <div key={f.key} className="flex items-center gap-2 flex-wrap text-sm">
                            <Gauge className="w-4 h-4 text-[#17A2B8]" />
                            <span>{f.label} :</span>
                            <input type="number" step={f.step} min={f.min} max={f.max}
                              defaultValue={row.params ? f.toDisp(row.params[f.key]) : ""}
                              key={`${f.key}-${row.params ? row.params[f.key] : ""}`}
                              data-testid={`param-${row.type}-${f.key}`}
                              onBlur={(e) => saveParam(row, f, e.target.value)}
                              className="w-24 rounded-md border border-border bg-secondary/40 px-2 py-1 text-sm outline-none focus:border-[#17A2B8]" />
                            <span className="text-muted-foreground">{f.unit}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Fréquence de répétition */}
                    <div className="flex items-center gap-2 flex-wrap text-sm">
                      <Timer className="w-4 h-4 text-[#F5821F]" />
                      <span>Répéter au maximum toutes les</span>
                      <input type="number" min={0} defaultValue={row.repeat_minutes} data-testid={`repeat-${row.type}`}
                        onBlur={(e) => patch(row.type, { repeat_minutes: Number(e.target.value) })}
                        className="w-20 rounded-md border border-border bg-secondary/40 px-2 py-1 text-sm outline-none focus:border-[#17A2B8]" />
                      <span>min <span className="text-muted-foreground">(0 = pas de limite)</span></span>
                    </div>

                    {/* Mise en veille */}
                    <div className="flex items-center gap-2 flex-wrap text-sm">
                      <Power className="w-4 h-4 text-[#F59E0B]" />
                      <span>Mettre en veille :</span>
                      {[["1 h", 60], ["8 h", 480], ["24 h", 1440]].map(([l, m]) => (
                        <button key={m} onClick={() => patch(row.type, { snooze_minutes: m })} data-testid={`snooze-${row.type}-${m}`}
                          className="px-2.5 py-1 rounded-md border border-border text-xs font-semibold hover:border-[#F59E0B] hover:text-[#F59E0B]">{l}</button>
                      ))}
                      {row.snooze_until && new Date(row.snooze_until) > new Date() && (
                        <button onClick={() => patch(row.type, { snooze_minutes: 0 })} data-testid={`snooze-clear-${row.type}`}
                          className="px-2.5 py-1 rounded-md border border-[#10B981] text-xs font-semibold text-[#10B981]">Réactiver maintenant</button>
                      )}
                    </div>

                    {/* Planning */}
                    <div className="text-sm">
                      <label className="flex items-center gap-2 cursor-pointer mb-2">
                        <input type="checkbox" checked={!!row.always_on} onChange={(e) => patch(row.type, { always_on: e.target.checked })} data-testid={`alwayson-${row.type}`} className="w-4 h-4 accent-[#17A2B8]" />
                        <CalendarClock className="w-4 h-4 text-[#8B5CF6]" /> Toujours actif (24h/7j)
                      </label>
                      {!row.always_on && (
                        <div className="pl-6 space-y-2">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs text-muted-foreground mr-1">Jours :</span>
                            {DAYS.map((d, i) => {
                              const on = (row.days || []).includes(i);
                              return (
                                <button key={i} data-testid={`day-${row.type}-${i}`}
                                  onClick={() => { const days = on ? row.days.filter((x) => x !== i) : [...(row.days || []), i]; patch(row.type, { days }, true); }}
                                  className={`w-7 h-7 rounded-md text-xs font-bold border ${on ? "bg-[#17A2B8] text-white border-[#17A2B8]" : "border-border text-muted-foreground"}`}>{d}</button>
                              );
                            })}
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-muted-foreground">De</span>
                            <select value={row.hour_start} onChange={(e) => patch(row.type, { hour_start: Number(e.target.value) }, true)} data-testid={`hstart-${row.type}`} className="rounded-md border border-border bg-secondary/40 px-2 py-1 outline-none">
                              {Array.from({ length: 25 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}h</option>)}
                            </select>
                            <span className="text-muted-foreground">à</span>
                            <select value={row.hour_end} onChange={(e) => patch(row.type, { hour_end: Number(e.target.value) }, true)} data-testid={`hend-${row.type}`} className="rounded-md border border-border bg-secondary/40 px-2 py-1 outline-none">
                              {Array.from({ length: 25 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}h</option>)}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Nettoyage historique + retour à l'hérité (A3) */}
                    <div className="pt-1 flex items-center gap-4 flex-wrap">
                      <button onClick={() => clearHistory(row)} disabled={!row.count} data-testid={`clear-history-${row.type}`}
                        className="flex items-center gap-1.5 text-xs font-semibold text-[#EF4444] hover:underline disabled:opacity-40 disabled:no-underline">
                        <Trash2 className="w-3.5 h-3.5" /> Vider l'historique ({row.count})
                      </button>
                      {scopeType !== "global" && row.has_override && (
                        <button onClick={() => revert(row)} data-testid={`revert-${row.type}`}
                          className="flex items-center gap-1.5 text-xs font-semibold text-[#17A2B8] hover:underline">
                          <RotateCcw className="w-3.5 h-3.5" /> Revenir au réglage hérité
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
